import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteImage } from '@/contexts/SiteImagesContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { LayoutDashboard, Calendar, Stethoscope, Briefcase, Users, Settings, Menu, LogOut, ArrowLeft, CalendarDays, Newspaper, Image, Database, DoorOpen, CalendarClock, Repeat2, MessageCircle, AlertTriangle, HeartPulse, Bot, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import safeStorage from '@/lib/safeStorage';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { ImpersonateButton } from '@/components/ImpersonationControl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIDEBAR_COLLAPSED_KEY = 'smb_backoffice_sidebar_collapsed';

// Gruppo principale (operatività quotidiana)
const SIDEBAR_MAIN = [
  { label: 'Dashboard', href: '/backoffice', icon: LayoutDashboard },
  { label: 'Appuntamenti', href: '/backoffice/appuntamenti', icon: Calendar },
  { label: 'Ricollocazioni', href: '/backoffice/ricollocazioni', icon: Repeat2, badgeKey: 'rebooking' },
  { label: 'WhatsApp', href: '/backoffice/whatsapp', icon: MessageCircle, badgeKey: 'whatsapp' },
  { label: 'Conversazioni AI', href: '/backoffice/conversazioni-ai', icon: Bot },
  { label: 'Dottori', href: '/backoffice/dottori', icon: Stethoscope },
  { label: 'Disponibilità', href: '/backoffice/disponibilita', icon: CalendarClock },
  { label: 'Pazienti', href: '/backoffice/pazienti', icon: HeartPulse },
  { label: 'Eventi', href: '/backoffice/eventi', icon: CalendarDays },
  { label: 'News', href: '/backoffice/news', icon: Newspaper },
];
// Gruppo configurazione (in basso, separato)
const SIDEBAR_CONFIG = [
  { label: 'Servizi', href: '/backoffice/servizi', icon: Briefcase },
  { label: 'Studi', href: '/backoffice/studi', icon: DoorOpen },
  { label: 'Utenti', href: '/backoffice/utenti', icon: Users },
  { label: 'Media', href: '/backoffice/media', icon: Image },
  { label: 'Impostazioni', href: '/backoffice/impostazioni', icon: Settings, adminOnly: true },
  { label: 'Backup', href: '/backoffice/backup', icon: Database, adminOnly: true },
];
const SIDEBAR_ITEMS = [...SIDEBAR_MAIN, ...SIDEBAR_CONFIG];

/** Voce di menu con tooltip quando la sidebar è compatta */
function NavItem({ to, onClick, active, icon: Icon, label, badge, badgeClass, collapsed, testId, danger = false, as = 'link' }) {
  const base = `flex items-center rounded-lg text-sm transition-colors duration-200 relative ${collapsed ? 'justify-center px-0 py-2.5 w-11 h-11 mx-auto' : 'gap-3 px-3 py-2.5 w-full'}`;
  const color = danger
    ? 'text-red-600 hover:bg-red-50'
    : active ? 'bg-[#238A92] text-white' : 'text-[#1F2A37] hover:bg-[hsl(var(--secondary))]';
  const inner = (
    <>
      <Icon size={18} className="flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
      {badge > 0 && (
        collapsed ? (
          <span className={`absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-white text-[#238A92]' : badgeClass}`} data-testid={testId}>{badge > 99 ? '99+' : badge}</span>
        ) : (
          <span className={`ml-auto text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${active ? 'bg-white text-[#238A92]' : badgeClass}`} data-testid={testId}>{badge}</span>
        )
      )}
    </>
  );
  const el = as === 'button'
    ? <button type="button" onClick={onClick} className={`${base} ${color}`} aria-label={label}>{inner}</button>
    : <Link to={to} onClick={onClick} className={`${base} ${color}`} aria-label={label} aria-current={active ? 'page' : undefined}>{inner}</Link>;
  if (!collapsed) return el;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{el}</TooltipTrigger>
      <TooltipContent side="right" className="text-xs font-medium">{label}{badge > 0 ? ` (${badge})` : ''}</TooltipContent>
    </Tooltip>
  );
}

function SidebarContent({ user, logoImg, isActive, onItemClick, logout, navigate, rebookingCount, whatsappCount, collapsed = false, onToggleCollapse = null }) {
  return (
    <TooltipProvider>
    <div className="flex flex-col h-full min-h-0">
      <div className={`py-3 border-b border-[hsl(var(--border))] flex-shrink-0 flex items-center justify-center min-h-[56px] ${collapsed ? 'px-2' : 'px-4'}`} data-testid="backoffice-sidebar-header">
        {collapsed
          ? <span className="w-9 h-9 rounded-lg bg-[#238A92]/10 text-[#238A92] flex items-center justify-center font-bold text-sm" style={{fontFamily:'Space Grotesk'}} title="Studio Medico Boscovich">SMB</span>
          : logoImg ? <img src={logoImg} alt="Studio Medico Boscovich" className="h-8" /> : <LayoutDashboard size={22} className="text-[#238A92]" />}
      </div>
      <nav className={`flex-1 min-h-0 overflow-y-auto space-y-1 sidebar-scroll ${collapsed ? 'p-2' : 'p-3'}`} data-testid="backoffice-sidebar-nav">
        {[...SIDEBAR_MAIN, { separator: true }, ...SIDEBAR_CONFIG].map((item, idx) => {
          if (item.separator) return <div key={`sep-${idx}`} className="my-2 border-t border-[hsl(var(--border))]" role="separator" data-testid="sidebar-separator" />;
          if (item.adminOnly && user?.role !== 'admin') return null;
          const badge = item.badgeKey === 'rebooking' ? rebookingCount : item.badgeKey === 'whatsapp' ? whatsappCount : 0;
          const badgeClass = item.badgeKey === 'rebooking' ? 'bg-amber-500 text-white' : 'bg-[#1E7A58] text-white';
          const testId = item.badgeKey === 'rebooking' ? 'rebooking-badge' : item.badgeKey === 'whatsapp' ? 'whatsapp-badge' : undefined;
          return (
            <NavItem key={item.href} to={item.href} onClick={onItemClick} active={isActive(item.href)} icon={item.icon} label={item.label} badge={badge} badgeClass={badgeClass} testId={testId} collapsed={collapsed} />
          );
        })}
      </nav>
      <div className={`border-t border-[hsl(var(--border))] space-y-1 flex-shrink-0 bg-white ${collapsed ? 'p-2' : 'p-3'}`}>
        {onToggleCollapse && (
          <NavItem as="button" onClick={onToggleCollapse} icon={collapsed ? PanelLeftOpen : PanelLeftClose} label={collapsed ? 'Espandi menu' : 'Riduci menu'} collapsed={collapsed} />
        )}
        <NavItem to="/" onClick={onItemClick} icon={ArrowLeft} label="Torna al Sito" collapsed={collapsed} />
        <NavItem as="button" onClick={() => { logout(); navigate('/'); }} icon={LogOut} label="Esci" collapsed={collapsed} danger />
      </div>
    </div>
    </TooltipProvider>
  );
}

export default function BackofficeLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rebookingCount, setRebookingCount] = useState(0);
  const [whatsappCount, setWhatsappCount] = useState(0);
  const [waHealth, setWaHealth] = useState(null);
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(SIDEBAR_COLLAPSED_KEY) === true);
  const toggleCollapsed = () => setCollapsed(c => { safeStorage.set(SIDEBAR_COLLAPSED_KEY, !c); return !c; });
  const logoImg = useSiteImage('logo_header');

  // Badge sidebar + controllo salute WhatsApp/WAHA (refresh 60s)
  useEffect(() => {
    let active = true;
    const fetchCount = () => {
      axios.get(`${API}/rebooking-tasks/count`)
        .then(r => { if (active) setRebookingCount(r.data?.da_gestire || 0); })
        .catch(() => {});
      axios.get(`${API}/whatsapp/unread-count`)
        .then(r => { if (active) setWhatsappCount(r.data?.unread || 0); })
        .catch(() => {});
      axios.get(`${API}/whatsapp/health`)
        .then(r => { if (active) setWaHealth(r.data); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/backoffice') return location.pathname === '/backoffice';
    return location.pathname.startsWith(href);
  };

  const sidebarProps = {
    user,
    logoImg,
    isActive,
    onItemClick: () => setMobileOpen(false),
    logout,
    navigate,
    rebookingCount,
    whatsappCount,
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:block bg-white border-r border-[hsl(var(--border))] h-screen fixed top-0 left-0 overflow-hidden transition-[width] duration-200 ${collapsed ? 'w-[68px]' : 'w-64'}`} data-testid="backoffice-sidebar" data-collapsed={collapsed ? 'true' : 'false'}>
        <SidebarContent {...sidebarProps} collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
      </aside>

      {/* Main area */}
      <div className={`flex-1 transition-[margin] duration-200 ${collapsed ? 'lg:ml-[68px]' : 'lg:ml-64'}`}>
        {/* Top bar */}
        <header className="bg-white border-b border-[hsl(var(--border))] px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <SidebarContent {...sidebarProps} />
              </SheetContent>
            </Sheet>
            <Button variant="ghost" size="icon" className="hidden lg:inline-flex text-muted-foreground hover:text-[#238A92]" onClick={toggleCollapsed} aria-label={collapsed ? 'Espandi menu' : 'Riduci menu'} title={collapsed ? 'Espandi menu' : 'Riduci menu'} data-testid="sidebar-collapse-toggle">
              {collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
            </Button>
            <h1 className="text-lg font-semibold" style={{fontFamily:'Space Grotesk'}}>
              {SIDEBAR_ITEMS.find(i => isActive(i.href))?.label || 'Backoffice'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <ImpersonateButton />
            <div className="text-sm text-muted-foreground hidden sm:block">
              {user?.email}
            </div>
          </div>
        </header>

        {/* Allarme salute WhatsApp/WAHA: visibile su ogni pagina del backoffice */}
        {waHealth?.monitored && !waHealth.healthy && (
          <div className="mx-4 sm:mx-6 mt-4 max-w-[1400px] lg:mx-auto rounded-lg bg-red-50 border border-red-300 px-4 py-3 flex items-start gap-3" data-testid="wa-health-banner">
            <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                {waHealth.reason === 'server_unreachable'
                  ? 'WhatsApp non operativo: il server WAHA non risponde'
                  : waHealth.reason === 'api_key_invalid'
                  ? 'WhatsApp non operativo: chiave API WAHA non valida'
                  : `WhatsApp non operativo: sessione ${waHealth.session === 'SCAN_QR_CODE' ? 'da collegare (scansiona il QR)' : `in stato ${waHealth.session || 'sconosciuto'}`}`}
              </p>
              <p className="text-xs text-red-700 mt-0.5">
                {waHealth.reason === 'server_unreachable'
                  ? 'Controlla che il PC con Docker sia acceso e che tunnel e container WAHA siano attivi (docker ps, docker logs waha-tunnel). I messaggi dei pazienti non vengono ricevuti finché il server è offline.'
                  : waHealth.reason === 'api_key_invalid'
                  ? 'Il server WAHA risponde ma rifiuta la chiave API salvata: probabilmente il container è stato riavviato con una WAHA_API_KEY diversa. Verifica la chiave del container Docker (docker inspect waha | grep WAHA_API_KEY) e aggiornala in Impostazioni → WhatsApp, oppure riavvia il container con la chiave originale.'
                  : 'Apri la Configurazione WhatsApp per riavviare la sessione o scansionare di nuovo il QR.'}
              </p>
            </div>
            <Link to="/backoffice/whatsapp/configurazione" className="text-xs font-semibold text-red-700 underline hover:text-red-900 flex-shrink-0" data-testid="wa-health-settings-link">
              Configurazione WhatsApp
            </Link>
          </div>
        )}

        {/* Content */}
        <div className={`p-4 sm:p-6 mx-auto ${collapsed ? 'max-w-[1600px]' : 'max-w-[1400px]'}`}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
